import React, { useCallback, useEffect, useRef, useState } from "react"
import PropTypes from "prop-types"
import { Redirect, Route, useLocation } from "react-router-dom"
import { connect } from "react-redux"
import { Auth } from "aws-amplify"

import { addToken, logoutUser } from "../../helpers/fakebackend_helper.js"
import { getProfile } from "../../store/auth/profile/actions"
import SessionGate from "../../components/Common/SessionGate"
import {
  ONBOARDING_GATE_ENABLED,
  ONBOARDING_ROUTE,
  isOnboardingEnforced,
} from "../../helpers/onboarding"

// How long to wait on the profile fetch before offering a Retry. Generous on
// purpose: logging out and straight back in as another user is a cold path —
// Cognito round-trip, then a fresh profile fetch — and a shorter threshold
// offered Retry while the request was still perfectly healthy.
const SLOW_PROFILE_MS = 20000

/**
 * Reads a usable session straight out of localStorage.
 *
 * A stored user with no token can never authorize a request, so it does not
 * count as a session: previously that case early-returned as valid and every
 * API call 401'd until expireSession lapsed four hours later.
 */
const readStoredSession = () => {
  try {
    const raw = localStorage.getItem("user")
    const stored = raw ? JSON.parse(raw) : null
    const token = localStorage.getItem("token")
    if (stored?.user && stored.expireSession > Date.now() && token) {
      return stored.user
    }
  } catch (err) {
    // Malformed localStorage; fall through and re-resolve against Cognito.
  }
  return null
}

const PrivateRoute = ({
  component: Component,
  layout: Layout,
  path,
  exact,
  profile,
  profileStatus,
  profileError,
  onGetProfile,
}) => {
  const location = useLocation()

  // Resolve synchronously when localStorage already holds a good session, so
  // ordinary navigation doesn't flash a hold screen on every route change.
  const [auth, setAuth] = useState(() => {
    const username = readStoredSession()
    return { state: username ? "ok" : "resolving", username }
  })
  const [slow, setSlow] = useState(false)

  // Gate on whether a profile is actually in hand, not on status.
  //
  // Fifteen pages dispatch getProfile on mount, and every dispatch flips status
  // back to "loading". Gating on status would therefore unmount the page that
  // just dispatched, which remounts and dispatches again — an endless loop. The
  // reducer leaves `user` intact across a refetch, so keying off the data means
  // background refetches never blank a page that is already usable.
  const hasProfile =
    profile && typeof profile === "object" && Object.keys(profile).length > 0

  useEffect(() => {
    if (auth.state === "ok") return undefined

    let cancelled = false
    const resolveSession = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser()
        addToken(user)
        const stored = JSON.parse(localStorage.getItem("user"))
        if (!cancelled) {
          setAuth({ state: "ok", username: stored?.user || null })
        }
      } catch (err) {
        await logoutUser()
        if (!cancelled) setAuth({ state: "expired", username: null })
      }
    }
    resolveSession()

    return () => {
      cancelled = true
    }
    // Mount-only: the initial auth value is read from the closure above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Which username this mount has already asked for. Guards against retry loops
  // without tying the fetch to a particular starting status: a failed login
  // leaves Profile.status at "error" in a store that survives the next, good
  // login, and keying the fetch off "idle" meant it was never retried.
  const attemptedFor = useRef(null)

  // The profile is fetched here rather than by individual pages, because the
  // onboarding gate has to read it before deciding where to send the user.
  useEffect(() => {
    if (auth.state !== "ok" || !auth.username) return
    if (hasProfile || profileStatus === "loading") return
    if (attemptedFor.current === auth.username) return
    attemptedFor.current = auth.username
    onGetProfile(auth.username)
  }, [auth.state, auth.username, hasProfile, profileStatus, onGetProfile])

  useEffect(() => {
    if (profileStatus !== "loading") {
      setSlow(false)
      return undefined
    }
    const timer = setTimeout(() => setSlow(true), SLOW_PROFILE_MS)
    return () => clearTimeout(timer)
  }, [profileStatus])

  const retry = useCallback(() => {
    if (auth.username) onGetProfile(auth.username)
  }, [auth.username, onGetProfile])

  const render = () => {
    if (auth.state === "resolving") return <SessionGate state="resolving" />
    if (auth.state === "expired" || !auth.username) {
      return <Redirect to="/login" />
    }

    if (!hasProfile) {
      // Only report a failure this mount actually caused. A status left behind
      // by an earlier session would otherwise surface as an error screen for a
      // user who has just logged in successfully and whose fetch is pending.
      const attempted = attemptedFor.current === auth.username

      // Cognito authenticated the user but no SAGE profile exists yet. There is
      // no profile to read a step from, so onboarding is not consulted here.
      if (attempted && profileStatus === "missing") {
        return (
          <SessionGate
            state="missing"
            detail={profileError?.message}
            onRetry={retry}
          />
        )
      }

      if (attempted && profileStatus === "error") {
        return (
          <SessionGate
            state="error"
            detail={profileError?.message}
            onRetry={retry}
          />
        )
      }

      return (
        <SessionGate
          state={slow ? "slow" : "loading"}
          onRetry={slow ? retry : undefined}
        />
      )
    }

    // Only a mandatory run holds the app shut. Someone who opted in from the
    // invite keeps the run of the app and can leave whenever they like.
    if (
      ONBOARDING_GATE_ENABLED &&
      isOnboardingEnforced(profile) &&
      location.pathname.indexOf(ONBOARDING_ROUTE) !== 0
    ) {
      return <Redirect to={ONBOARDING_ROUTE} />
    }

    return (
      <Layout>
        <Component />
      </Layout>
    )
  }

  return <Route path={path} exact={exact} render={render} />
}

PrivateRoute.propTypes = {
  component: PropTypes.any,
  layout: PropTypes.any,
  path: PropTypes.any,
  exact: PropTypes.any,
  profile: PropTypes.any,
  profileStatus: PropTypes.string,
  profileError: PropTypes.any,
  onGetProfile: PropTypes.func,
}

const mapStateToProps = state => ({
  profile: state.Profile?.user || null,
  profileStatus: state.Profile?.status || "idle",
  profileError: state.Profile?.error || null,
})

const mapDispatchToProps = dispatch => ({
  onGetProfile: username => dispatch(getProfile(username)),
})

export default connect(mapStateToProps, mapDispatchToProps)(PrivateRoute)
